'use client'

import { Button, ButtonLink, html } from '@ensemble.music/ui'
import { ArrowDown, Check } from 'lucide-react'
import { motion } from 'motion/react'
import { exampleOffer, hero, occasions } from './content'
import { Parallax } from './Parallax'

// Opacity and transform only, so nothing in here can shift the layout. The H1 is deliberately
// NOT animated: it is the LCP element and must be painted on the first frame.
const rise = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
}

const step = (index: number) => ({
  duration: 0.3,
  delay: 0.08 * index,
  ease: 'easeOut' as const,
})

export function Hero({ onStart }: { onStart: (occasion?: string) => void }) {
  return (
    <html.section className="flex flex-row flex-wrap items-start gap-16 w-full max-sm:gap-10">
      <html.div className="flex flex-col gap-6 grow shrink basis-[420px]">
        <html.p className="text-sm font-semibold uppercase tracking-wide text-primary-text">
          {hero.overline}
        </html.p>

        <html.h1 className="text-5xl font-semibold leading-tight tracking-tight text-color max-md:text-4xl max-sm:text-3xl">
          {hero.headline}
        </html.h1>

        <motion.div
          {...rise}
          transition={step(1)}
        >
          <html.p className="text-lg text-color-secondary max-w-[560px]">{hero.subline}</html.p>
        </motion.div>

        <motion.div
          {...rise}
          transition={step(2)}
        >
          <html.div className="flex flex-row flex-wrap items-center gap-3">
            <Button
              type="button"
              size="lg"
              onClick={() => onStart()}
            >
              {hero.primaryCta}
            </Button>
            <ButtonLink
              href="#so-funktionierts"
              tone="ghost"
              size="lg"
            >
              {hero.secondaryCta}
              <ArrowDown
                size={20}
                aria-hidden="true"
              />
            </ButtonLink>
          </html.div>
        </motion.div>

        <motion.div
          {...rise}
          transition={step(3)}
        >
          <html.ul className="flex flex-row flex-wrap gap-x-6 gap-y-2">
            {hero.checks.map((check) => (
              <html.li
                key={check}
                className="flex flex-row items-center gap-2 text-base text-color-secondary"
              >
                <html.span className="text-success">
                  <Check
                    size={18}
                    aria-hidden="true"
                  />
                </html.span>
                {/* react-native-web wants a text element next to an element child, see AGENTS 4a */}
                <html.span className="text-base text-color-secondary">{check}</html.span>
              </html.li>
            ))}
          </html.ul>
        </motion.div>

        <motion.div
          {...rise}
          transition={step(4)}
        >
          <html.div className="flex flex-col gap-2">
            <html.p
              id="anlass-chips-label"
              className="text-sm text-color-tertiary"
            >
              Worum geht es?
            </html.p>
            <html.div
              aria-labelledby="anlass-chips-label"
              className="flex flex-row flex-wrap gap-2"
            >
              {occasions.map((occasion) => (
                <html.button
                  key={occasion}
                  type="button"
                  onClick={() => onStart(occasion)}
                  className="inline-flex flex-row items-center min-h-[44px] px-4 rounded-full border border-border-color-control bg-surface text-base hover:bg-primary-soft hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring"
                >
                  <html.span className="text-base text-color">{occasion}</html.span>
                </html.button>
              ))}
            </html.div>
          </html.div>
        </motion.div>
      </html.div>

      <motion.div
        className="landing-hero-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.45, ease: 'easeOut' }}
      >
        {/* Drifts a little slower than the text column while the hero scrolls away. */}
        <Parallax distance={36}>
          <ExampleOfferCard />
        </Parallax>
      </motion.div>
    </html.section>
  )
}

/** Shows what a booker receives. Fictional and labelled, never a real price or a real date. */
function ExampleOfferCard() {
  return (
    <html.div className="flex flex-col gap-4 w-full bg-surface border border-border-color rounded-card shadow-lg p-6 max-sm:p-4">
      <html.div className="flex flex-row">
        <html.span className="inline-flex flex-row items-center px-3 py-1 rounded-full bg-info-soft text-sm font-semibold text-on-info-soft">
          {exampleOffer.badge}
        </html.span>
      </html.div>

      <html.div className="flex flex-col gap-1">
        <html.p className="text-xl font-semibold text-color">{exampleOffer.bandName}</html.p>
        <html.p className="text-base text-color-secondary">{exampleOffer.tagline}</html.p>
      </html.div>

      {/* muted autoplay is what browsers allow; controls give the pause WCAG 2.2.2 asks for */}
      {/* html.* has no video, so the frame carries the classes */}
      <html.div className="w-full aspect-video rounded-xl overflow-hidden bg-background-muted">
        <video
          src="/videos/band-playing.mp4"
          poster="/videos/band-playing.jpg"
          aria-label={exampleOffer.videoCaption}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          autoPlay
          muted
          loop
          playsInline
          controls
          preload="metadata"
        />
      </html.div>

      {/* html.* has no dl/dt/dd (see @tamagui/dom TagName), so this is a list of rows. */}
      <html.ul className="flex flex-col gap-2">
        {exampleOffer.rows.map((row) => (
          <html.li
            key={row.label}
            className="flex flex-row justify-between gap-4 py-2 border-b border-border-color-subtle"
          >
            <html.span className="text-base text-color-tertiary">{row.label}</html.span>
            <html.span className="text-base font-semibold text-color text-right">
              {row.value}
            </html.span>
          </html.li>
        ))}
      </html.ul>

      <html.p className="flex flex-row items-center gap-2 text-success">
        <Check
          size={18}
          aria-hidden="true"
        />
        <html.span className="text-base font-semibold text-success">
          {exampleOffer.availability}
        </html.span>
      </html.p>

      <html.p className="text-sm text-color-tertiary">{exampleOffer.disclaimer}</html.p>
    </html.div>
  )
}
